// 店铺商品相关

import DropShippingProductType from "@/enum/DropShippingProductType";
import TaskStatusEnum from "@/enum/TaskStatus";
import platformServer, { get, post } from "@/services/http/platformServer";
import { PremiumFunctionType } from "@/services/PremiumValidator";
import { WrapperList } from "@/utils/utilityTypes";

export enum ProductStatus {
    DELETED = "DELETED",
    IN_STOCK = "IN_STOCK",
    ON_SALE = "ON_SALE",
}

export interface DropShippingAssociation {
    id: number;
    supplierShopId: number;
    supplierSkuId: number;
    lastUpdated: string;
}

export interface ProductDTO {
    barcode: string;
    creationTime: string;
    externalId: string;
    favorite: boolean;
    id: number;
    modificationTime: string;
    originalId: string;
    picture: string;
    shopId: number;
    simpleTitle: string;
    skus: ProductSkuDTO[];
    status: ProductStatus;
    stockNo: string;
    title: string;
    url: string;
    /**
     * 单位 克
     */
    weight: number;
    type: DropShippingProductType;
    dropShippingAssociations: DropShippingAssociation[];
    /**自动分包设置 */
    maxQuantityPerPackage?: number;
    market?: string;
    wholesaler?: string;

    cost: number;
}

export interface ProductSkuDTO {
    id: number;
    barcode: string;
    externalId: string;
    originalId: string;
    picture: string;
    /**价格 */
    price: number;
    propertiesId: string;
    propertiesName: string;
    simpleTitle: string;
    status: "DELETED" | "NORMAL";
    stockNo: string;
    /**
     * 单位 克
     */
    weight: number;
    type: DropShippingProductType;
    dropShippingAssociations: DropShippingAssociation[];
    /**自动分包设置 */
    maxQuantityPerPackage?: number;
    market?: string;
    wholesaler?: string;

    cost: number;
}

export enum SearchLevel {
    ALL = "ALL",
    PRODUCT = "PRODUCT",
    SKU = "SKU",
}

export enum TitleSearchLevel {
    PRODUCT_TITLE = "PRODUCT_TITLE",
    PRODUCT_SIMPLE_TITLE = "PRODUCT_SIMPLE_TITLE",
    PRODUCT_TITLE_AND_SIMPLE_TITLE = "PRODUCT_TITLE_AND_SIMPLE_TITLE",
}

export enum SkuTitleSearchLevel {
    SKU_SIMPLE_TITLE = "SKU_SIMPLE_TITLE",
    SKU_PROPERTIES_NAME = "SKU_PROPERTIES_NAME",
    SKU_SIMPLE_TITLE_AND_PROPERTIES_NAME = "SKU_SIMPLE_TITLE_AND_PROPERTIES_NAME",
}

export interface GetProductListParams {
    shopId: number;
    pageNo: number;
    pageSize: number;
    externalId?: string;
    status?: ProductStatus;
    /**货位 */
    stockNo?: string;
    /**标题 */
    title?: string;
    skuTitle?: string;
    /**只显示为设置简称的商品 */
    noSimpleTitle?: boolean;
    /**只显示未设置货位的商品 */
    noStockNo?: boolean;
    /**只显示未设置重量的商品 */
    noWeight?: boolean;
    noWholesaler?: boolean;
    noMarket?: boolean;
    hasPendingShipmentOrder?: boolean;
    originalId?: string;
    /** 是否收藏商品 */
    favorite?: boolean;
    /**是否设置了自动分包规则 */
    maxQuantityPerPackageSet?: boolean;
    /** 是否显示删除的商品 */
    showDeleted?: boolean;

    /**
     * 商品名称搜索级别，默认为 ALL（商品和 SKU 全部级别搜索）
     */
    titleSearchLevel?: TitleSearchLevel;
    /**
     * 货位号搜索级别，默认为 ALL（商品和 SKU 全部级别搜索）
     */
    stockNoSearchLevel?: SearchLevel;
    /**
     * 商品编码搜索级别，默认为 ALL（商品和 SKU 全部级别搜索）
     */
    externalIdSearchLevel?: SearchLevel;

    skuTitleSearchLevel?: SkuTitleSearchLevel;
}

// 获取店铺商品列表
export function getList(params: GetProductListParams) {
    return platformServer.get<{ products: ProductDTO[]; total: number }>(params.shopId, `/product/list`, params);
}

// 删除product
export function deleteProductByIds({ ids = [], shopId }: any) {
    return platformServer.post(shopId, "/product/deleteByIds", {
        ids,
        shopId,
    });
}

export interface ProductSyncProcess {
    errorMsg: string;
    shopId: number;
    status: "COMPLETED" | "ERROR" | "RUNNING";
    lastSyncProductTime?: string;
}

// 店铺商品同步
export function sync(shopId: number) {
    return platformServer.post<ProductSyncProcess>(shopId, `/product/sync`, { shopId });
}

export function getSyncProductProgress(shopId: number) {
    return platformServer.post<ProductSyncProcess>(shopId, "/product/sync_process", {
        shopId,
    });
}

interface SyncProductByIdsParams {
    originalIds: string[];
    shopId: number;
}

// 根据商品ids同步
export function syncByProductIds({ originalIds = [], shopId }: SyncProductByIdsParams) {
    return platformServer.post<{ successful: boolean }>(shopId, `/product/syncByIds`, { originalIds, shopId });
}

// 更新商品简称
export function updateProductSimpleTitle({ shopId, simpleTitle, productId }) {
    return platformServer.post(shopId, "/product/updateProductSimpleTitle", { simpleTitle, shopId, productId });
}

// 更新商品货位
export function updateProductStockNo({ shopId, stockNo, productId }) {
    return platformServer.post(shopId, "/product/updateProductStockNo", { stockNo, shopId, productId });
}

// 更新商品重量
export function updateProductWeight({ shopId, productId, weight }) {
    return platformServer.post(shopId, "/product/updateProductWeight", { shopId, productId, weight });
}

// 更新商品市场
export function updateProductMarket({ shopId, productId, market }) {
    return platformServer.post(shopId, "/product/update_product_market", { shopId, productId, market });
}

// 更新商品档口
export function updateProductWholesaler({ shopId, productId, wholesaler }) {
    return platformServer.post(shopId, "/product/update_product_wholesaler", { shopId, productId, wholesaler });
}

// 更新商品成本价
export function updateProductCost({ shopId, productIds, cost }) {
    return platformServer.post(shopId, "/product/update_product_cost", { shopId, productIds, cost });
}

// 更新sku简称
export function updateSkuSimpleTitle({ shopId, skuId, simpleTitle, productId }) {
    return platformServer.post(shopId, "/product/updateSkuSimpleTitle", { shopId, skuId, simpleTitle, productId });
}

// 更新sku货位
export function updateSkuStockNo({ shopId, skuId, stockNo, productId }) {
    return platformServer.post(shopId, "/product/updateSkuStockNo", { shopId, skuId, stockNo, productId });
}

// 更新sku重量
export function updateSkuWeight({ shopId, skuId, weight, productId }) {
    return platformServer.post(shopId, "/product/updateSkuWeight", { shopId, skuId, weight, productId });
}

// 更新Sku市场
export function updateSkuMarket({ shopId, skuId, productId, market }) {
    return platformServer.post(shopId, "/product/update_sku_market", { shopId, skuId, productId, market });
}

// 更新Sku档口
export function updateSkuWholesaler({ shopId, skuId, productId, wholesaler }) {
    return platformServer.post(shopId, "/product/update_sku_wholesaler", { shopId, productId, wholesaler, skuId });
}

// 更新Sku档口
export function updateSkuCost({ shopId, skuId, productId, cost }) {
    return platformServer.post(shopId, "/product/update_sku_cost", { shopId, productId, cost, skuId });
}

interface DropshippingProductListParams {
    externalId?: string;

    pageNo: number;

    productOriginalIds?: string[];

    pageSize: number;

    shopId: number;

    status?: string;

    supplierShopIds?: number[];

    title?: string;

    notBoundToAnySupplier?: boolean;
}

/** 列出当前店铺的商品，供商品关联页面使用  */
export function dropshippingProductList(params: DropshippingProductListParams): Promise<WrapperList<ProductDTO>> {
    return platformServer.post(params.shopId, "/drop_shipping_product/list", params);
}

interface BatchQueryProductByIdsParams {
    productIds: number[];

    shopId: number;
}

/** 通过商品id列表获取商品信息  */
export function batchQueryProductByIds(params: BatchQueryProductByIdsParams): Promise<ProductDTO[]> {
    return post(params.shopId, "/drop_shipping_product/query_by_product_ids", params);
}

interface UpdateSkuMaxQuantityPerPackageParams {
    maxQuantityPerPackage?: number;
    shopId: number;
    skuId: number;
}

/**
 * 更新sku的分包设置
 */
export function updateSkuMaxQuantityPerPackage(params: UpdateSkuMaxQuantityPerPackageParams) {
    return post(params.shopId, "/product/update_sku_max_quantity_per_package", params, {
        pathPremiumValidator() {
            return {
                shopId: params.shopId,
                type: PremiumFunctionType.MULTI_PACKAGE_PRINTING,
            };
        },
    });
}

interface UpdateProductMaxQuantityPerPackageParams {
    maxQuantityPerPackage: number;
    productId: number;
    shopId: number;
}

/**
 * 更新product的分包设置
 */
export function updateProductMaxQuantityPerPackage(params: UpdateProductMaxQuantityPerPackageParams) {
    return post(params.shopId, "/product/update_product_max_quantity_per_package", params, {
        pathPremiumValidator() {
            return {
                shopId: params.shopId,
                type: PremiumFunctionType.MULTI_PACKAGE_PRINTING,
            };
        },
    });
}

export interface ProductMaxQuantityPerPackageDto {
    id: number;
    originalId: string;
    maxQuantityPerPackage: number;
    /**只会返回设置过maxQuantityPerPackage属性的Sku */
    skus?: ProductSkuMaxQuantityPerPackageDto[];
}

export interface ProductSkuMaxQuantityPerPackageDto {
    id: number;
    originalId: string;
    maxQuantityPerPackage: number;
}

/**
 * 获取店铺内素有设置过自动分包的商品
 */
export function getAllMaxQuantityPerPackageSetProducts(shopId: number) {
    return post<ProductMaxQuantityPerPackageDto[]>(shopId, "/product/get_all_max_quantity_per_package_set_products", {
        shopId,
    });
}

interface BatchUpdateProductWeightParams {
    productIds: number[];
    shopId: number;
    weight: number;
}

export function batchUpdateProductWeight(params: BatchUpdateProductWeightParams): Promise<{ successful: boolean }> {
    return post(params.shopId, "/product/batch_update_product_weight", params);
}

interface BatchUpdateProductMarketWholesalerParams {
    productIds: number[];
    shopId: number;
    market: string;
    wholesaler: string;
}

export function batchUpdateProductMarketWholesaler(
    params: BatchUpdateProductMarketWholesalerParams,
): Promise<{ successful: boolean }> {
    return post(params.shopId, "/product/batch_update_product_market_and_wholesaler", params);
}

interface BatchProductSearchResultBatchProductSearchResult {
    error?: string;
    shopId: number;
    products: ProductDTO[];
}

interface BatchSearchProductParams extends Omit<GetProductListParams, "shopId"> {
    shopIds: number[];
}

export function batchSearchProduct(
    params: BatchSearchProductParams,
): Promise<BatchProductSearchResultBatchProductSearchResult[]> {
    return post(params.shopIds[0], "/product/batch_search", params);
}

interface ProductSkuLogParams {
    shopId: number;
    pageSize: number;
    pageNo: number;
    /** 商品ID 或者 SKU ID */
    productSkuId: number;
}

export enum ProductSkuOperationType {
    MODIFY_PRODUCT_SIMPLE_TITLE = "MODIFY_PRODUCT_SIMPLE_TITLE",
    MODIFY_SKU_SIMPLE_TITLE = "MODIFY_SKU_SIMPLE_TITLE",
    MODIFY_PRODUCT_STOCK_NO = "MODIFY_PRODUCT_STOCK_NO",
    MODIFY_SKU_STOCK_NO = "MODIFY_SKU_STOCK_NO",
    MODIFY_PRODUCT_WEIGHT = "MODIFY_PRODUCT_WEIGHT",
    MODIFY_SKU_WEIGHT = "MODIFY_SKU_WEIGHT",
    MODIFY_PRODUCT_MARKET = "MODIFY_PRODUCT_MARKET",
    MODIFY_SKU_MARKET = "MODIFY_SKU_MARKET",
    MODIFY_PRODUCT_WHOLESALER = "MODIFY_PRODUCT_WHOLESALER",
    MODIFY_SKU_WHOLESALER = "MODIFY_SKU_WHOLESALER",
}

interface ProductSkuLog {
    id: number;
    shopId: number;
    productId: number;
    skuId: number;
    type: "PRODUCT" | "SKU";
    productOriginalId: string;
    skuOriginalId: string;
    operationType: ProductSkuOperationType;
    operation: string;
    operator: string;
    dateCreated: string;
}

export function getProductSkuLog(params: ProductSkuLogParams) {
    return post<WrapperList<ProductSkuLog>>(params.shopId, "/product_sku_log/list_sku", params);
}

export function getProductLog(params: ProductSkuLogParams) {
    return post<WrapperList<ProductSkuLog>>(params.shopId, "/product_sku_log/list_product", params);
}

export interface SubmitProductExportTaskParams {
    /** 需要导出的选中的商品查询参数，『勾选商品的情况下』需要传递该参数， */
    productSelectParams?: {
        shopId: number;
        productIds: number[];
    };
    /** 需要导出的商品列表查询参数，『未勾选商品的情况下』需要传递该参数，用于实现导出当前查询的商品列表，最多导出前 5000 条 */
    productListParams?: GetProductListParams;
}

export interface ProductExportTask {
    downloadUrl: string;
    id: string;
    shopId: number;
    status?: string;
}
export function submitProductExportTask(shopId: number, params: SubmitProductExportTaskParams) {
    return post<ProductExportTask>(shopId, "/product_export_task/export", params);
}

/** 查询导出商品信息任务结果，用于轮询导出任务结果 */
export function queryProductExportTask(shopId: number, taskId: string) {
    return post<ProductExportTask>(shopId, `/product_export_task/export/${taskId}`, { taskId });
}

export interface AsyncImportProductSettingParams {
    shopId: number;
    file: File;
}

interface ProductSettingImportResultErrorRow {
    /**
     * 错误信息
     */
    message?: string;
    /**
     * 错误行号
     */
    rowNum?: number;
}

export interface ProductSettingImportResult {
    /**
     * 导入失败的信息
     */
    errors?: ProductSettingImportResultErrorRow[];
    /**
     * 成功导入的数量
     */
    successCount?: number;
}

export interface ProductSettingImportTask {
    error?: string;
    id: string;
    result: ProductSettingImportResult;
    shopId: number;
    taskStatus: TaskStatusEnum;
}

/** 导入商品设置 */
export function asyncImportProductSetting(params: AsyncImportProductSettingParams) {
    const { shopId, file } = params;

    const formData = new FormData();
    formData.append("file", file);

    return post<ProductSettingImportTask>(shopId, `/product/async_import_setting`, formData, {
        params: {
            shopId,
        },
    });
}

export interface QnAsyncImportProductSettingParams {
    shopId: number;
    excelFileUrl: string;
    excelFileOriginalName: string;
    excelFileSize: number;
}

export function qnAsyncImportProductSetting(params: QnAsyncImportProductSettingParams) {
    const { shopId, excelFileUrl, excelFileOriginalName, excelFileSize } = params;

    return post<ProductSettingImportTask>(shopId, `/product/qianniu/async_import_setting`, {
        shopId,
        excelFileUrl,
        excelFileOriginalName,
        excelFileSize,
    });
}

export function getProductSettingImportResult(shopId: number, taskId: string) {
    return get<ProductSettingImportTask>(shopId, `/product/setting_import_result`, {
        taskId,
    });
}

interface SaveManualSkuDTO {
    /**
     * 在{@link OperateDataFrom.SEARCH_FROM_OTHER_SHOPS} 时，该字段为必填字段，用于标识数据来源的原始商品 ID。
     */
    sourceOriginalId?: string;

    /**
     * Sku 编码
     */
    externalId?: string;
    /**
     * 平台原始ID
     */
    originalId?: string;
    /**
     * 图片
     */
    picture?: string;
    /**
     * 单品价格，可以为空。
     */
    price?: number;
    /**
     * Sku属性、规格
     */
    propertiesName?: string;
    /**
     * 单品重量（KG），可以为空。
     */
    weight?: number;
}

export interface SaveManualProductDTO {
    /**
     * 在{@link OperateDataFrom.SEARCH_FROM_OTHER_SHOPS} 时，该字段为必填字段，用于标识数据来源的原始商品 ID。
     */
    sourceOriginalId?: string;
    /**
     * 在{@link OperateDataFrom#SEARCH_FROM_OTHER_SHOPS} 时，该字段为必填字段，用于标识数据来源的店铺 ID。
     */
    sourceShopId?: number;

    /**
     * 商品编码
     */
    externalId?: string;
    /**
     * 平台原始商品ID
     */
    originalId?: string;
    /**
     * 图片
     */
    picture?: string;
    /**
     * Sku信息列表
     */
    skus?: SaveManualSkuDTO[];
    /**
     * 标题
     */
    title?: string;
}

/**
 * 商品数据来源。
 */
export enum OperateDataFrom {
    MANUAL = "MANUAL",
    SEARCH_FROM_OTHER_SHOPS = "SEARCH_FROM_OTHER_SHOPS",
}

interface SaveManualProductParams {
    shopId: number;
    operateDataFrom: OperateDataFrom;
    products: SaveManualProductDTO[];
}

/** 新增/更新手工店铺新增商品信息。 */
export function saveManualProduct(params: SaveManualProductParams) {
    return post(params.shopId, "/product/batch_save_manual_products", params);
}

export default {
    getList,
    deleteProductByIds,
    sync,
    getSyncProductProgress,
    syncByProductIds,
    updateProductSimpleTitle,
    updateProductStockNo,
    updateProductWeight,
    updateSkuSimpleTitle,
    updateSkuStockNo,
    updateSkuWeight,
};
